import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Box, FlexBox } from '../box';
import { useSize } from '../hooks';
import { styled } from '../styled';
import { Typography } from '../typography';
import { makeEnum, withVersion } from '../utils';
const DATA_LIST_PADDING = {
    compact: [0.5, 2],
    standard: [1, 2],
    expanded: [2, 2]
};
const DATA_LIST_GAP = {
    compact: 0,
    standard: 0.25,
    expanded: 0.5
};
const DATA_LIST_HORIZONTAL_GAP = {
    compact: 1,
    standard: 2,
    expanded: 2
};
const DATA_LIST_ITEMS_MIN_HEIGHT = {
    compact: 3.75,
    standard: undefined,
    expanded: 3.75
};
export const DataListOrientation = makeEnum('vertical', 'horizontal');
export const DataListDensity = makeEnum('compact', 'standard', 'expanded');
const VerticalRow = styled(FlexBox)(({ theme, $minHeight }) => ({
    width: '100%',
    minHeight: $minHeight ? theme.spacing($minHeight) : undefined,
    overflow: 'hidden'
}));
const HorizontalRow = styled(FlexBox)(({ theme, $minHeight }) => ({
    width: '100%',
    minHeight: $minHeight ? theme.spacing($minHeight) : undefined,
    overflow: 'hidden'
}));
const LabelCell = styled(FlexBox)(() => ({
    minWidth: 0
}));
const ValueCell = styled(FlexBox)(() => ({
    minWidth: 0
}));
const DataListRow = ({ headerName, value, orientation, density, padding, skeleton, divider }) => {
    const borderTop = divider ? 1 : undefined;
    const borderColor = divider ? 'divider' : undefined;
    const label = (_jsx(Typography, { color: "text.secondary", textOverflow: "clip", whiteSpace: "normal", textTransform: "sentence", children: headerName }));
    if (orientation === 'vertical') {
        return (_jsxs(VerticalRow, { stack: true, padding: padding, borderTop: borderTop, borderColor: borderColor, gap: DATA_LIST_GAP[density], "$minHeight": DATA_LIST_ITEMS_MIN_HEIGHT[density], children: [_jsx(LabelCell, { children: label }), _jsx(ValueCell, { children: renderValue({ value, skeleton }) })] }));
    }
    return (_jsxs(HorizontalRow, { padding: padding, borderTop: borderTop, borderColor: borderColor, gap: DATA_LIST_HORIZONTAL_GAP[density], vAlign: "center", "$minHeight": DATA_LIST_ITEMS_MIN_HEIGHT[density], children: [_jsx(LabelCell, { stretch: true, children: label }), _jsx(ValueCell, { flexShrink: 0, children: renderValue({ value, skeleton }) })] }));
};
const DataListImpl = (props) => {
    const { row, columns, orientation = 'vertical', density = 'standard', disableGutters = false, divider = false, hideNull = false, columnWidth = '100%', className, 'data-role': dataRole = 'data-list', 'data-role-value': dataRoleValue } = props;
    const { ref, rect } = useSize();
    const skeleton = !row;
    const padding = disableGutters
        ? [DATA_LIST_PADDING[density][0], 0]
        : DATA_LIST_PADDING[density];
    const resolvedColumnWidth = getColumnWidth(columnWidth);
    const columnsPerRow = Math.max(1, Math.floor((rect?.width ?? resolvedColumnWidth) / resolvedColumnWidth));
    const showDivider = divider && columnsPerRow === 1;
    const rows = columns
        .map((column) => ({
        column,
        value: resolveColumnValue(column, row)
    }))
        .filter(({ value }) => !(hideNull && (value === null || value === undefined)));
    return (_jsx(FlexBox, { minWidth: "100%", width: "100%", stack: true, children: _jsx(Box, { ref: ref, display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${typeof columnWidth === 'number' ? `${columnWidth}px` : columnWidth}, 1fr))`, alignItems: "start", "data-role": dataRole, "data-role-value": dataRoleValue, className: className, children: rows.map(({ column, value }, index) => (_jsx(DataListRow, { headerName: column.headerName, value: value, orientation: orientation, density: density, padding: padding, skeleton: skeleton, divider: showDivider && index > 0 }, column.field ?? column.headerName))) }) }));
};
export const DataList = withVersion(DataListImpl, '1.0.0');
DataList.displayName = 'DataList';
function resolveColumnValue(column, row) {
    if (!row) {
        return null;
    }
    if (column.renderCell) {
        return column.renderCell({ row });
    }
    if (!column.field) {
        return null;
    }
    return row[column.field];
}
function renderValue({ value, skeleton }) {
    if (skeleton) {
        return (_jsx(Typography, { fontWeight: 500, skeleton: true, whiteSpace: "normal", children: "NO_CONTENT" }));
    }
    if (isTypographyCompatible(value)) {
        return (_jsx(Typography, { color: "text.primary", fontWeight: 500, textOverflow: "clip", whiteSpace: "normal", children: value ?? ' ' }));
    }
    return value;
}
function isTypographyCompatible(content) {
    return typeof content === 'string' || typeof content === 'number';
}
function getColumnWidth(columnWidth) {
    if (typeof columnWidth === 'number') {
        return columnWidth;
    }
    const parsedColumnWidth = Number.parseInt(columnWidth, 10);
    return Number.isNaN(parsedColumnWidth)
        ? Number.MAX_SAFE_INTEGER
        : parsedColumnWidth;
}
